import React, { useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box, CircularProgress } from '@mui/material';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Products from './pages/Products';
import Inventory from './pages/Inventory';
import Lookup from './pages/Lookup';
import ActivityLog from './pages/ActivityLog';
import Users from './pages/Users';
import LoginPage from './pages/LoginPage';
import { useAuth } from './services/auth';

// Gaming-themed dark blue theme with mobile responsiveness
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#00BFFF', // Deep sky blue
      dark: '#0080FF', // Brighter blue
      light: '#40E0D0', // Turquoise
    },
    secondary: {
      main: '#FF6B35', // Orange accent
      dark: '#FF4500',
      light: '#FFA500',
    },
    background: {
      default: '#0A0E1A', // Very dark blue
      paper: '#1A1F2E', // Dark blue-gray
    },
    text: {
      primary: '#E0E6ED', // Light blue-white
      secondary: '#A0B1C5', // Muted blue-gray
    },
    error: {
      main: '#FF4757',
    },
    warning: {
      main: '#FFA502',
    },
    success: {
      main: '#2ED573',
    },
    info: {
      main: '#00BFFF',
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  spacing: (factor) => `${0.25 * factor}rem`,
  typography: {
    fontFamily: [
      '"Orbitron"',
      '"Roboto"',
      'monospace',
      'Arial',
      'sans-serif',
    ].join(','),
    // Responsive typography
    h1: {
      fontWeight: 700,
      textShadow: '0 0 10px #00BFFF',
      fontSize: 'clamp(1.75rem, 5vw, 3rem)',
    },
    h2: {
      fontWeight: 600,
      textShadow: '0 0 8px #00BFFF',
      fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
    },
    h3: {
      fontWeight: 600,
      textShadow: '0 0 6px #00BFFF',
      fontSize: 'clamp(1.25rem, 3vw, 2rem)',
    },
    h4: {
      fontWeight: 500,
      textShadow: '0 0 4px #00BFFF',
      fontSize: 'clamp(1.1rem, 2.5vw, 1.5rem)',
    },
    h5: {
      fontWeight: 500,
      fontSize: 'clamp(1rem, 2vw, 1.25rem)',
    },
    h6: {
      fontWeight: 500,
      fontSize: 'clamp(0.9rem, 1.5vw, 1.1rem)',
    },
    body1: {
      fontSize: 'clamp(0.875rem, 2vw, 1rem)',
    },
    body2: {
      fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)',
    },
    button: {
      textTransform: 'none',
      fontSize: 'clamp(0.8rem, 2vw, 0.95rem)',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(145deg, #1A1F2E 0%, #2A2F3E 100%)',
          border: '1px solid rgba(0, 191, 255, 0.2)',
          boxShadow: '0 8px 32px rgba(0, 191, 255, 0.1)',
          '&:hover': {
            border: '1px solid rgba(0, 191, 255, 0.4)',
            boxShadow: '0 12px 40px rgba(0, 191, 255, 0.2)',
            transform: 'translateY(-2px)',
          },
          transition: 'all 0.3s ease-in-out',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
          padding: '10px 20px',
          background: 'linear-gradient(45deg, #00BFFF 30%, #0080FF 90%)',
          boxShadow: '0 3px 15px rgba(0, 191, 255, 0.3)',
          color: '#1a237e', // Dark blue text color
          minHeight: '44px', // Touch-friendly minimum height
          '&:hover': {
            background: 'linear-gradient(45deg, #0080FF 30%, #00BFFF 90%)',
            boxShadow: '0 5px 20px rgba(0, 191, 255, 0.4)',
            transform: 'translateY(-1px)',
            color: '#1a237e', // Maintain dark blue text on hover
          },
          transition: 'all 0.2s ease-in-out',
          '@media (max-width: 600px)': {
            padding: '12px 16px',
            fontSize: '0.9rem',
            minWidth: '48px',
          },
        },
        outlined: {
          color: '#1a237e', // Dark blue text for outlined buttons
          borderColor: '#1a237e', // Dark blue border
          '&:hover': {
            color: '#1a237e', // Maintain dark blue text on hover
            borderColor: '#1a237e', // Maintain dark blue border on hover
            backgroundColor: 'rgba(26, 35, 126, 0.04)', // Subtle background on hover
          },
        },
        text: {
          color: '#1a237e', // Dark blue text for text buttons
          '&:hover': {
            color: '#1a237e', // Maintain dark blue text on hover
            backgroundColor: 'rgba(26, 35, 126, 0.04)', // Subtle background on hover
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(90deg, #0A0E1A 0%, #1A1F2E 50%, #0A0E1A 100%)',
          border: 'none',
          borderBottom: '2px solid rgba(0, 191, 255, 0.3)',
          boxShadow: '0 4px 20px rgba(0, 191, 255, 0.2)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(145deg, #1A1F2E 0%, #2A2F3E 100%)',
          border: '1px solid rgba(0, 191, 255, 0.1)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            minHeight: '44px', // Touch-friendly height
            '& fieldset': {
              borderColor: 'rgba(0, 191, 255, 0.3)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(0, 191, 255, 0.5)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#00BFFF',
              boxShadow: '0 0 10px rgba(0, 191, 255, 0.3)',
            },
          },
          '@media (max-width: 600px)': {
            '& .MuiOutlinedInput-root': {
              fontSize: '16px', // Prevents zoom on iOS
            },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            padding: '12px', // Larger touch target
            '&.MuiIconButton-sizeSmall': {
              padding: '8px',
            },
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          '@media (max-width: 900px)': {
            display: 'none', // Hide tables on mobile, replace with cards
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            '& .MuiDialog-paper': {
              margin: '8px',
              width: 'calc(100% - 16px)',
              maxHeight: 'calc(100% - 16px)',
            },
          },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            paddingLeft: '8px',
            paddingRight: '8px',
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          // Custom scrollbar styling for webkit browsers
          '&::-webkit-scrollbar': {
            width: '12px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(0, 191, 255, 0.1)',
            borderRadius: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'linear-gradient(45deg, #00BFFF 30%, #0080FF 90%)',
            borderRadius: '6px',
            border: '2px solid rgba(0, 191, 255, 0.1)',
            '&:hover': {
              background: 'linear-gradient(45deg, #0080FF 30%, #00BFFF 90%)',
            },
          },
          '&::-webkit-scrollbar-corner': {
            background: 'rgba(0, 191, 255, 0.1)',
          },
          // Firefox scrollbar styling
          scrollbarWidth: 'thin',
          scrollbarColor: '#00BFFF rgba(0, 191, 255, 0.1)',
        },
        // Apply to all elements with scrollbars
        '*': {
          '&::-webkit-scrollbar': {
            width: '12px',
            height: '12px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(0, 191, 255, 0.1)',
            borderRadius: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'linear-gradient(45deg, #00BFFF 30%, #0080FF 90%)',
            borderRadius: '6px',
            border: '2px solid rgba(0, 191, 255, 0.1)',
            '&:hover': {
              background: 'linear-gradient(45deg, #0080FF 30%, #00BFFF 90%)',
            },
          },
          '&::-webkit-scrollbar-corner': {
            background: 'rgba(0, 191, 255, 0.1)',
          },
          // Firefox scrollbar styling
          scrollbarWidth: 'thin',
          scrollbarColor: '#00BFFF rgba(0, 191, 255, 0.1)',
        },
      },
    },
  },
});

function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #0A0E1A 0%, #1A1F2E 50%, #0A0E1A 100%)',
            backgroundAttachment: 'fixed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={60} />
        </Box>
      </ThemeProvider>
    );
  }

  if (!user) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #0A0E1A 0%, #1A1F2E 50%, #0A0E1A 100%)',
            backgroundAttachment: 'fixed',
          }}
        >
          <LoginPage />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #0A0E1A 0%, #1A1F2E 50%, #0A0E1A 100%)',
          backgroundAttachment: 'fixed',
        }}
      >
        <Layout>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/products" element={<Products />} />
            <Route path="/inventory" element={<Inventory />} />
            <Route path="/lookup" element={<Lookup />} />
            <Route path="/activity-log" element={<ActivityLog />} />
            <Route path="/users" element={<Users />} />
          </Routes>
        </Layout>
      </Box>
    </ThemeProvider>
  );
}

export default App;